import React, { memo, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import Close from '@mui/icons-material/Close';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Download from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import JobStatusChip, { JOB_STATUS_META } from './JobStatusChip';
import { DashboardJob, formatCreated, formatDuration, hasFiles, itemsSummary } from './jobModel';
import { monoSx, pillButtonSx, sectionLabelSx } from './styles';

interface JobDetailsDrawerProps {
  job: DashboardJob | undefined;
  downloading: boolean;
  onClose: () => void;
  onDownload: (job: DashboardJob) => void;
  onDelete: (id: string) => void;
}

const fileName = (path: string) => path.split(/[\\/]/).pop() || path;

function JobFacts({ job }: { job: DashboardJob }) {
  const { day, time } = formatCreated(job.createdAt);
  const facts: Array<readonly [string, string]> = [
    ['Requested by', job.userId ?? '—'],
    ['Created', `${day}, ${time}`],
    ['Duration', formatDuration(job)],
    ...(job.items ? [['Items', itemsSummary(job.items)] as const] : []),
    ...job.facts,
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 1.5, columnGap: 1, fontSize: 13 }}>
      {facts.map(([label, value]) => (
        <React.Fragment key={label}>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {label}
          </Box>
          <Box component="span" sx={{ wordBreak: 'break-word' }}>
            {value}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

function JobFiles({ files }: { files: string[] }) {
  return (
    <Box>
      <Typography sx={{ ...sectionLabelSx, mb: 1 }}>Files</Typography>
      {files.length === 0 ? (
        <Box sx={{ fontSize: 13, color: 'text.secondary', p: 1.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 3 }}>
          Files appear here once the job produces output.
        </Box>
      ) : (
        files.map((file) => (
          <Box
            key={file}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, mb: 1, border: 1, borderColor: 'divider', borderRadius: 3 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.dark',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
              }}
            >
              <DescriptionOutlined fontSize="small" />
            </Box>
            <Typography noWrap title={file} sx={{ ...monoSx, fontSize: 12, minWidth: 0 }}>
              {fileName(file)}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
}

function JobDetails({ job, downloading, onClose, onDownload, onDelete }: Omit<JobDetailsDrawerProps, 'job'> & { job: DashboardJob }) {
  const statusColor = JOB_STATUS_META[job.status].color;
  const downloadable = hasFiles(job);
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 3, pb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>{job.typeLabel}</Typography>
          <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {job.partnerName}
          </Typography>
          <Box sx={{ mt: 1.25, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <JobStatusChip status={job.status} />
            <Typography component="span" sx={{ ...monoSx, fontSize: 12, color: 'text.secondary', wordBreak: 'break-all' }}>
              {job.id}
            </Typography>
          </Box>
        </Box>
        <IconButton aria-label="Close details" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {job.status === 'failed' && job.error ? (
          <Box
            role="alert"
            sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.75, borderRadius: 3, bgcolor: (theme) => alpha(theme.palette.error.main, 0.1) }}
          >
            <ErrorIcon color="error" fontSize="small" />
            <Box sx={{ fontSize: 13, lineHeight: 1.5 }}>
              <Box sx={{ fontWeight: 600, mb: 0.25 }}>Job failed</Box>
              {job.error}
            </Box>
          </Box>
        ) : null}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, mb: 1 }}>
            <Box component="span" sx={{ fontWeight: 500 }}>
              Progress
            </Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {Math.round(job.progress)}%
            </Box>
          </Box>
          <LinearProgress variant="determinate" value={job.progress} color={statusColor} sx={{ height: 6, borderRadius: 3 }} />
        </Box>
        <JobFacts job={job} />
        <JobFiles files={job.files} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="outlined" color="error" onClick={() => onDelete(job.id)} sx={{ ...pillButtonSx, fontWeight: 500 }}>
          Delete
        </Button>
        <Button
          variant="contained"
          disabled={!downloadable || downloading}
          startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <Download />}
          onClick={() => onDownload(job)}
          sx={{ ...pillButtonSx, ml: 'auto' }}
        >
          Download all
        </Button>
      </Box>
    </>
  );
}

const JobDetailsDrawer: React.FC<JobDetailsDrawerProps> = ({ job, ...rest }) => {
  // Keep the last job rendered while the drawer animates closed (or after the job is deleted).
  const lastJob = useRef(job);
  if (job) lastJob.current = job;
  const shown = job ?? lastJob.current;
  return (
    <Drawer
      anchor="right"
      open={Boolean(job)}
      onClose={rest.onClose}
      PaperProps={{
        sx: { width: 440, maxWidth: '100vw', borderRadius: '16px 0 0 16px', display: 'flex', flexDirection: 'column' },
      }}
    >
      {shown ? <JobDetails job={shown} {...rest} /> : null}
    </Drawer>
  );
};

export default memo(JobDetailsDrawer);
